// @flow
import { createContext, useContext, useState } from 'react';
import axios from 'axios';
import { getPageLocale } from './PageContext';

type CuisineStateObjectType = { [string]: string, __refresh__?: boolean };
type StateType = [CuisineStateObjectType, (CuisineStateObjectType) => void];

export const CuisinesContext = createContext<StateType>([{}, () => {}]);

const CACHED_CUISINES_KEY = 'cuisines-cache';
const CACHED_CUISINES_EXPIRY = 'cuisines-expiry';
const REFRESH_KEY = '__refresh__';

export const Provider = ({ children }: { children: any }) => {
  const locale = getPageLocale();
  let cachedCuisines = { [REFRESH_KEY]: true };
  if (typeof window !== 'undefined') {
    try {
      const cachedJsonString = localStorage.getItem(
        `${locale}-${CACHED_CUISINES_KEY}`
      );
      const cachedExpiry = localStorage.getItem(
        `${locale}-${CACHED_CUISINES_EXPIRY}`
      );
      if (cachedJsonString && cachedExpiry) {
        cachedCuisines = JSON.parse(cachedJsonString);
        const lastLoad = new Date(cachedExpiry);
        cachedCuisines[REFRESH_KEY] =
          new Date().getTime() - lastLoad.getTime() > 1000 * 60 * 60 * 24;
      }
    } catch (e) {
      cachedCuisines[REFRESH_KEY] = true;
    }
  }
  const [cuisines, setCuisines] = useState(cachedCuisines);
  return (
    <CuisinesContext.Provider
      value={[
        cuisines,
        (newCuisines: CuisineStateObjectType) => setCuisines(newCuisines)
      ]}
    >
      {children}
    </CuisinesContext.Provider>
  );
};

const hasRefreshedCuisinesByLocale = {};

export function getCuisineTranslations(keys: string[]): string[] {
  const locale = getPageLocale();
  const [cuisines, setCuisines] = useContext(CuisinesContext);
  if (cuisines[REFRESH_KEY] && !hasRefreshedCuisinesByLocale[locale]) {
    hasRefreshedCuisinesByLocale[locale] = true;
    axios
      .get(`${CONFIG.dinerApi}/v2/cuisines?locale=${locale}`)
      .then(({ data: { cuisines: cuisinesArray } }) => {
        let hasUpdatedCuisine = false;
        const newCuisines = {};
        cuisinesArray.forEach(
          ({ field: rawField, text_translations: translations }) => {
            const [{ translation: value }] = translations;
            const field = rawField.replace(/-+/gi, '_');
            if (!cuisines[field] || cuisines[field] !== value) {
              hasUpdatedCuisine = true;
            }
            newCuisines[field] = value;
          }
        );
        if (!hasUpdatedCuisine) return;
        setCuisines(newCuisines);
        if (typeof window !== 'undefined') {
          localStorage.setItem(
            `${locale}-${CACHED_CUISINES_KEY}`,
            JSON.stringify(newCuisines)
          );
          localStorage.setItem(
            `${locale}-${CACHED_CUISINES_EXPIRY}`,
            new Date().toISOString()
          );
        }
      });
  }
  return keys.map((key) => cuisines[key] || key);
}
